import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Inbox, ArrowLeft, Lock, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { adminLogin, verifyToken, fetchMessages } from "../lib/apiClient";

const TOKEN_KEY = "nh_admin_token";

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminLogin(email, password);
      localStorage.setItem(TOKEN_KEY, token);
      onSuccess(token);
      toast.success("Welcome back, Natalia.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-deep flex items-center justify-center px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm bg-white/[0.04] border border-white/10 rounded-sm p-8"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-berry text-white">
            <Lock size={18} />
          </span>
          <div>
            <h1 className="font-serif text-2xl text-white">Admin Access</h1>
            <p className="text-xs text-white/50">Contact inbox</p>
          </div>
        </div>
        <label className="label-caps text-white/50 block mb-2">Email</label>
        <Input
          data-testid="admin-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@domain.com"
          className="rounded-sm bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-berry h-11 mb-5"
        />
        <label className="label-caps text-white/50 block mb-2">Password</label>
        <Input
          data-testid="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="rounded-sm bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-berry h-11 mb-8"
        />
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={loading}
          className="w-full bg-berry hover:bg-berry-light disabled:opacity-60 text-white py-3 rounded-sm transition-colors duration-200 flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in...</> : "Sign in"}
        </button>
        <Link to="/" className="block text-center text-xs text-white/40 hover:text-white/70 mt-6 transition-colors">
          ← Back to site
        </Link>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(null);
  const [checking, setChecking] = useState(true);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  useEffect(() => {
    document.title = "Inbox — Admin";
    const saved = localStorage.getItem(TOKEN_KEY);
    if (!saved) {
      setChecking(false);
      return;
    }
    verifyToken(saved)
      .then(() => setToken(saved))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const loadMessages = useCallback((t) => {
    setLoadingMsgs(true);
    fetchMessages(t)
      .then(setMessages)
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setLoadingMsgs(false));
  }, []);

  useEffect(() => {
    if (token) loadMessages(token);
  }, [token, loadMessages]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMessages([]);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-navy-deep flex items-center justify-center text-white/60">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  if (!token) return <LoginForm onSuccess={setToken} />;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-5xl px-6 py-16">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-berry transition-colors">
            <ArrowLeft size={16} /> Back to site
          </Link>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-berry transition-colors"
          >
            <LogOut size={16} /> Log out
          </button>
        </div>

        <div className="flex items-center gap-3 mb-10">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-navy text-white"><Inbox size={22} /></span>
          <div>
            <h1 className="font-serif text-3xl text-navy">Contact Inbox</h1>
            <p className="text-sm text-slate-500">{messages.length} message{messages.length !== 1 ? "s" : ""}</p>
          </div>
        </div>

        {loadingMsgs ? (
          <div className="flex items-center gap-3 text-slate-400 py-16"><Loader2 className="animate-spin" size={20} /> Loading...</div>
        ) : messages.length === 0 ? (
          <div className="text-slate-400 py-16 text-center border border-dashed border-slate-300 rounded-sm">No messages yet.</div>
        ) : (
          <div className="space-y-4">
            {messages.map((m) => (
              <div key={m.id} data-testid={`message-${m.id}`} className="bg-white border border-slate-200 rounded-sm p-6 overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0 max-w-full">
                    <span className="font-medium text-navy break-all">{m.name}</span>
                    <a href={`mailto:${m.email}`} className="text-berry text-sm ml-3 break-all">{m.email}</a>
                  </div>
                  <span className="text-xs text-slate-400 shrink-0">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                {m.subject && <p className="mt-2 text-sm font-medium text-slate-700 break-words">{m.subject}</p>}
                <p className="mt-2 text-slate-600 leading-relaxed whitespace-pre-wrap break-words max-h-64 overflow-y-auto" style={{ overflowWrap: "anywhere" }}>{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
